import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { z } from 'zod/v3';
import { err, ok, Result } from 'neverthrow';
import { ToolCallModel } from '../tool-call.model';
import { toast } from '@cotera/client/app/components/ui';
import { Constant, Expression, MakeStruct } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { FormValues } from '../erod-schema-form/form-values';

export class ToolCallColumnPreviewAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Run Column Preview';
  inputSchema = z.object({});
  shortcut = ['ctrl', 'enter'];
  group = COLUMN_ACTION_GROUP;
  icon = 'play';

  applicable(context: ApplicableContext): boolean {
    return (
      context.hasTargetedModelOfType(ToolCallModel) &&
      context.targetedModelOfType(ToolCallModel)!.formState.snapshot().tool !==
        null
    );
  }

  async execute(
    _: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(ToolCallModel);

    Assert.assert(model !== null);

    const name = model.currentNameInGraph.snapshot();

    const rel = model.datasetRel.snapshot()!.map((r) => {
      Assert.assert(r.t === 'rel');

      return r.rel;
    })!;

    Assert.assert(rel.isOk());

    const parameters = model.formState.snapshot().parameters;

    const makeSelectData = (): Result<
      { deps: string[]; parametersExpression: Expression },
      { t: string; message?: string }[]
    > => {
      if (parameters && !isParametersEmpty(parameters)) {
        const parametersExpression = model.parametersExpression.snapshot();

        if (!parametersExpression) {
          return err([{ t: 'parse-error', message: 'No prompt expression' }]);
        }

        if (parametersExpression.isErr()) {
          toast.error(parametersExpression.error.message);
          return err([
            { t: 'parse-error', message: parametersExpression.error.message },
          ]);
        }

        const deps = Object.keys(
          parametersExpression.value.attrReqs.from ?? {}
        );

        return ok({
          deps,
          parametersExpression: parametersExpression.value,
        });
      }

      return ok({
        deps: [],
        parametersExpression: Constant('test'),
      });
    };

    const dataRes = makeSelectData();

    if (dataRes.isErr()) {
      toast.error('Invalid prompt');
      return err([
        {
          t: 'error',
          message: dataRes.error.join(', '),
        },
      ]);
    }

    const { deps } = dataRes.value;

    // const whereExpr = model.whereExpression.snapshot();

    const programEraQL = model.programEraQL.snapshot();

    if (programEraQL.isErr()) {
      toast.error(JSON.stringify(programEraQL.error));
      return err([
        {
          t: 'error',
          message: JSON.stringify(programEraQL.error),
        },
      ]);
    }

    const eraQL = programEraQL.value;

    const tool = model.formState.snapshot().tool;
    Assert.assert(tool !== null);

    const toolName = tool.name.replaceAll('/', '_');

    toast.success(`Running preview for ${name}`);
    const previewRel = rel.value
      // .where((_) => (whereExpr ? whereExpr : Constant(true)))
      .select((t) => ({
        ...deps
          .map((d) => ({ [d]: t.attr(d) }))
          .reduce((acc, curr) => ({ ...acc, ...curr }), {}),
        [`${name}_mapping`]: MakeStruct({
          [toolName]: MakeStruct({
            toolName: toolName,
            toolId: tool.id,
          }),
        }),
        [`${name}_eraql`]: eraQL,
      }))
      // .where((t) => t.attr(name).isNotNull())
      .select(
        (t) => ({
          ...t.star(),
        }),
        {
          distinct: true,
          limit: 300,
        }
      );

    model.preview.set(previewRel);

    return ok({});
  }
}

/**
 * Required: If parameters is a fieldTree; it must be a struct.
 */
function isParametersEmpty(parameters: FormValues) {
  switch (parameters.t) {
    case 'fieldTree': {
      Assert.assert('struct' in parameters.fieldTree);
      return Object.keys(parameters.fieldTree.struct).length === 0;
    }

    case 'eraQL':
      return parameters.eraQL.length > 0;

    case 'colRef':
      return parameters.colRef.length > 0;

    default:
      return Assert.unreachable(parameters.t);
  }
}
